import { css } from 'styled-components';
import type { Brand } from '@/domainLogic/index.ts';
import { MODAL_DESKTOP_BREAKPOINT_PX, MODAL_MOBILE_BREAKPOINT_PX } from '@/app/components/search/utils/useSsrMedia.ts';
import { mobileSortOption } from '@/app/components/search/components/Filters/HorizontalFilters/Pill/styled.ts';
import { OffersContainer } from '@/app/components/search/components/Card/Details/Features/styled.ts';

export const sortModalMobile = css<{ brand?: Brand }>`
  &.openx-ui-sort-modal-mobile {
    .container-item {
      padding: 0;
      overflow: hidden;
      border-radius: 9px 9px 0 0;
      display: flex;

      > div {
        width: 100%;
      }

      .oss-ui-form {
        padding: 0;
        @media only screen and (min-width: 768px) {
          width: 100%;
        }
      }

      .container-controls {
        padding: 0;

        .openx-ui-sort-modal {
          padding: 0 16px;
          ${mobileSortOption};

          > label {
            display: none;
          }

          > div {
            height: auto;
            min-height: 56px;
            padding: 16px 0;

            > label {
              font-size: ${({ theme }) => theme.kallisto.font.size['200']};
              font-weight: ${({ theme }) => theme.kallisto.font.weight.regular};
              line-height: ${({ theme }) => theme.kallisto.font.lineHeight['300']};
              height: auto;
              min-height: 24px;
            }

            .note {
              font-size: ${({ theme }) => theme.kallisto.font.size['175']};
              font-weight: ${({ theme }) => theme.kallisto.font.weight.regular};
              line-height: ${({ theme }) => theme.kallisto.font.lineHeight['250']};
              color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};

              a {
                text-decoration: underline;
                color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
              }
            }
          }
        }
      }
    }
  }
`;

const customDatesPickerDesktopStyles = css`
  [class*='DialogHeader__HeaderContainer'] {
    box-shadow: none !important;

    > div {
      box-shadow: none !important;
    }
  }

  [class*='Dialogstyles__Footer'] {
    box-shadow: none !important;
    padding: 16px;

    > div {
      justify-content: flex-end;
    }
  }

  [class*='Dialogstyles__Content'] {
    padding-top: 8px !important;
    padding-bottom: 0 !important;
    border-top: 1px solid #eaeaeb;
    box-shadow: none !important;
    max-width: 100%;
  }

  .oss-ui-calendar {
    > div {
      margin-bottom: 0;
    }
  }
`;

const customDatesPickerMobileStyles = css`
  @media (max-width: 767px) {
    max-width: 100% !important;
    max-height: calc(100% - 32px);
  }
`;

export const inlineDatePickerModal = css`
  &.inline-date-picker-modal {
    ${customDatesPickerDesktopStyles};
  }
`;

export const inlineDatePickerModalMobile = css`
  &.inline-date-picker-modal-mobile {
    ${customDatesPickerMobileStyles};
  }
`;

export const customCheckinCheckoutModal = css`
  &.custom-checkin-checkout-modal {
    ${customDatesPickerDesktopStyles};
  }
`;

export const customCheckinCheckoutMobileModal = css`
  &.custom-checkin-checkout-mobile-modal {
    ${customDatesPickerMobileStyles};
  }
`;

export const filterModal = css`
  &.filters-modal {
    [class^='DialogFooter__FooterContainerStyled'] {
      border-bottom-right-radius: 0;
    }
  }
`;

export const travelExtraPackModal = css`
  &.travel-extra-pack-modal {
    [class*='Dialogstyles__Content'] {
      > div {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        padding: 0;

        ul {
          margin-top: 16px;
          margin-bottom: 16px;
          margin-left: 0;
          width: 100%;

          li {
            align-items: center;
            position: relative;
            justify-content: space-between;
            min-height: 36px;
            margin-bottom: 0;
            width: 100%;

            &:before {
              content: unset;
            }

            &.divider {
              div:nth-child(2) {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                border-color: ${({ theme }) => theme.kallisto.color.common.black[100]};
              }

              &:nth-child(3) {
                div:nth-child(2) {
                  border-style: solid;
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const comparisonModal = css`
  &.comparison-modal {
    [class*='DialogHeader__HeaderContainer'] {
      box-shadow: unset !important;
    }

    [class*='Dialogstyles__Body'] {
      max-height: calc(100vh - 56px);
    }

    @media (max-width: 767px) {
      [class*='Dialogstyles__Content'] {
        padding: 0;
      }
    }
  }
`;

export const roomDetailsModal = css`
  &.room-details-modal {
    [class*='Dialogstyles__Content'] {
      padding: 0;
    }

    [class*='Dialogstyles__Body'] {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
    }
  }
`;

export const mobileModalFullHeight = css`
  &.mobile-modal-full-height {
    [class*='Dialogstyles__Content'] {
      padding: 0;
    }

    [class*='Dialogstyles__Body'] {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
    }
  }
`;

export const hotelReviewsModal = css`
  &.hotel-reviews-modal {
    [class*='Dialogstyles__Content'] {
      padding: 0;
    }

    min-height: calc(100% - 32px);

    &.hotel-reviews-modal--compact {
      min-height: unset;
    }
  }
`;

export const hotelModalLateral = css`
  &.hotel-modal-lateral {
    @media (min-width: 768px) {
      width: 768px;
      left: auto;
      right: 0;
      top: 0;
      bottom: 0;
      transform: none;
      border-radius: 0;
      max-height: 100%;
    }
  }
`;

export const hotelModalLateralEvo = css`
  &.hotel-modal-lateral-evo {
    @media (min-width: 768px) {
      width: 928px;
      left: auto;
      right: 0;
      top: 0;
      bottom: 0;
      transform: none;
      border-radius: 0;
      max-height: 100%;

      > div:first-child {
        box-shadow: none;
      }
    }
  }
`;

export const packageDetailsModal = css`
  &.package-details-modal {
    [class*='Dialogstyles__Content'] {
      padding: 0;
    }

    [class*='Dialogstyles__Header'] {
      box-shadow: none;
      border-bottom: 0;

      [class*='DialogHeader__HeaderContainer'] {
        border-bottom: 0;
      }
    }
  }
`;

export const hotelMapModal = css`
  &.hotel-map-modal {
    [class*='Dialogstyles__Content'] {
      padding: 0;
    }

    [class*='Dialogstyles__Body'] {
      height: calc(100vh - 56px);
    }
  }
`;

export const flexibleAlternativeDatesModal = css`
  ${OffersContainer} {
    display: none;
  }

  &.flexible-alternative-dates-evo {
    [class*='DialogHeader__HeaderContainer'] {
      box-shadow: none !important;
    }
    [class*='Dialogstyles__Footer'] {
      box-shadow: none;
      border-top: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
    }
  }

  &.flexible-alternative-dates-evo--scrollable {
    [class*='Dialogstyles__Footer'] {
      box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumTopShadow};
    }
  }
`;

export const peopleSelectorModal = css`
  &.custom-people-selector-modal {
    [class*='Dialogstyles__Content'] {
      .accordion {
        overflow: visible;

        .listbox {
          border-radius: 8px;

          [id^='lb_button_'] {
            ${({ theme }) => theme.kallisto.mixins.font('Body')};
            color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
            height: 52px !important;
            border-top: unset !important;
            border-bottom: unset !important;
            box-shadow: unset !important;
          }

          [id^='lb_list_'] {
            position: static;
            box-shadow: unset !important;

            li {
              padding-top: 8px;
              padding-bottom: 8px;
              height: 40px;
              min-height: unset;
              ${({ theme }) => theme.kallisto.mixins.font('Body')};
            }
          }

          &.open {
            box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.mediumDownShadow};

            [id^='lb_button_'] {
              ${({ theme }) => theme.kallisto.mixins.font('Title5')};
              height: 40px !important;
              cursor: default;
            }
          }
        }
      }
    }
  }
`;

export const hotelGalleryModal = css`
  &.hotel-gallery-modal {
    /* Right-anchored side-sheet (fullScreen on mobile, matches hotel-modal-lateral-evo width on desktop) */
    top: 0 !important;
    bottom: 0 !important;
    left: auto !important;
    right: 0 !important;
    transform: none;
    /* Capped at the viewport: the 928px sheet below would otherwise hang off the left edge of tablets
       narrower than that (MPH-748). */
    max-width: 100% !important;
    width: 100%;
    height: 100vh !important;
    height: 100dvh !important;
    border-radius: 0 !important;

    @media (min-width: 768px) {
      width: 928px;
      border-left: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral} !important;
      box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow} !important;
    }

    /* Photo (lightbox) view: expand to full viewport, no side-sheet chrome */
    &.is-photo {
      @media (min-width: 768px) {
        width: 100%;
        border-left: none !important;
        box-shadow: none !important;
      }
    }

    /* Body must NOT scroll at container level — LeftArea scrolls internally */
    [class*='Dialogstyles__Content'] {
      padding: 0;
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    [class*='Dialogstyles__Body'] {
      overflow: hidden;
      padding: 0;
    }
  }
`;

export const roomListModal = css`
  &.room-list-modal {
    [class*='Dialogstyles__Content'] {
      padding: 0;
      overflow: hidden;
      height: 100%;
    }

    height: 100%;

    [class*='DialogHeader__HeaderContainer'] {
      box-shadow: unset !important;
    }

    [class*='Dialogstyles__Body'] {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
  }
`;

export const packageSavingsModal = css`
  &.price-saving-modal {
    border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};

    [class*='Dialogstyles__Content'] {
      padding: 0 24px 32px;

      @media (max-width: ${MODAL_MOBILE_BREAKPOINT_PX}px) {
        padding: 0 16px 16px;
      }
    }

    @media (min-width: ${MODAL_DESKTOP_BREAKPOINT_PX}px) {
      box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
    }

    @media (max-width: ${MODAL_MOBILE_BREAKPOINT_PX}px) {
      box-shadow: ${({ theme }) => theme.kallisto.shadow.largeTopShadow};
    }
  }
`;

export const voucherModalEvo = css`
  &.voucher-modal {
    [class*='DialogHeader__HeaderContainer'] {
      box-shadow: unset !important;
    }
  }

  &.voucher-modal-evo {
    [class*='Dialogstyles__Footer'] {
      box-shadow: none !important;
    }
  }
`;
